'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import dateMask from '@egalite/ui/dateMask';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/personalData1';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';
import BooleanRadioGroup from '@egalite/ui/Form/BooleanRadioGroup';

const PersonalData1Form: FC<{ edit?: boolean; onSuccess?: () => void }> = ({ edit = false, onSuccess = null }) => {
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(initialValues);

	const router = useRouter();

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: candidateData,
		validationSchema,
		mutation: data => save('/api/v1/candidate/personalData1', data),
		onSuccess:
			onSuccess != null
				? () => onSuccess()
				: () => {
						router.push(`/candidato/form/personalData2`);
				  },
	});

	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					if (data.success === true) {
						const newValues = Object.keys(initialValues).reduce((acc, key) => {
							acc[key] = data.data[key] ?? initialValues[key];
							return acc;
						}, {});
						setCandidateData(newValues as typeof initialValues);
						setLoadingData(false);
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={2}
			formActualStep={1}
			formTotalSteps={2}
			progress={50}
			formTitle={'Dados pessoais'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					<InputField
						id="birthdate"
						type="text"
						label="Data de nascimento"
						maskOptions={dateMask}
						{...getFieldProps('birthdate')}
					/>

					<SelectField
						id="gender"
						label="Gênero"
						options={[
							{ value: 'Feminino', label: 'Feminino' },
							{ value: 'Masculino', label: 'Masculino' },
							{ value: 'Prefiro não responder', label: 'Prefiro não responder' },
							{ value: 'Outro', label: 'Personalizar' },
						]}
						{...getFieldProps('gender')}
					/>
					{['Outro'].includes(formController.values.gender) ? (
						<InputField id="genderOther" type="text" label="Descrição do gênero" {...getFieldProps('genderOther')} />
					) : null}

					<BooleanRadioGroup
						label={'Você possui CNH (carteira nacional de habilitação)?'}
						{...getFieldProps({ name: 'cnhHas', type: 'checkbox' })}
						buttonSize="small"
						labelStyle={{ marginBottom: 8 }}
					/>

					{formController.values.cnhHas === true ? (
						<SelectField
							id="cnhType"
							label="Tipo CNH"
							options={[
								{ value: 'A', label: 'A' },
								{ value: 'B', label: 'B' },
								{ value: 'C', label: 'C' },
								{ value: 'D', label: 'D' },
								{ value: 'E', label: 'E' },
								{ value: 'AB', label: 'AB' },
								{ value: 'AC', label: 'AC' },
								{ value: 'AD', label: 'AD' },
								{ value: 'AE', label: 'AE' },
							]}
							{...getFieldProps('cnhType')}
						/>
					) : null}

					<ErrorList errors={formController.errors} touched={formController.touched} />

					{errors != null &&
						errors.map((err, index) => (
							<Typography key={`err-${index}`} variant="body2" color="error.dark">
								{err}
							</Typography>
						))}
				</div>
				<div className="md:mt-24 mt-16">
					{edit ? (
						<Button type="submit" variant="primary" className="w-full">
							Salvar
						</Button>
					) : (
						<ButtonsPages hideBack={true} />
					)}
				</div>
			</form>
		</FormPage>
	);
};

export default PersonalData1Form;
